import { useEffect } from "react";

import { useNavigate } from "react-router-dom";

import { MULTISIG_ORG_STATUS } from "@/constants/multisigEnrollment";
import { Routes } from "@/constants/settings";

import { useCoSigningStatus } from "@/apiQueries/multisigEnrollment";

import {
  dismissEnrollmentOutcome,
  trackEnrollmentSession,
  useEnrollmentOutcome,
} from "@/helpers/enrollmentOutcome";
import { dismissUnenrollmentOutcome, useUnenrollmentOutcome } from "@/helpers/unenrollmentOutcome";

import { useCoSigningPause } from "@/hooks/useCoSigningPause";
import { useRedux } from "@/hooks/useRedux";

import { EnrollmentOutcomeBanner } from "./EnrollmentOutcomeBanner";
import { UnenrollmentOutcomeBanner } from "./UnenrollmentOutcomeBanner";
import { UnenrollmentPendingBanner } from "./UnenrollmentPendingBanner";

/**
 * The multisig banners above the Settings cards: a co-signing setup that
 * ended without turning on (dismissible), the outcome of the last turn-off
 * request (dismissible) and the live pause (not dismissible). The Owner who
 * started the request gets no pause banner; their status lives on the card.
 *
 * Owners only. The backend serves the pending request and the user roster to
 * Owners alone, so a Financial Controller can neither see a pause nor record
 * an outcome; see `useCoSigningPause`. Their banners from the design return
 * once the backend opens those reads.
 */
export const SettingsUnenrollmentBanners = () => {
  const navigate = useNavigate();
  const { profile } = useRedux("profile");
  const role = profile.data.role ?? null;
  const { isPaused, pause } = useCoSigningPause();
  const outcome = useUnenrollmentOutcome();
  const enrollmentOutcome = useEnrollmentOutcome();
  const coSigning = useCoSigningStatus({ userId: profile.data.id, role });

  // Follow the active setup so an unfinished one can be explained later.
  const session = coSigning?.data?.enrollment ?? null;
  const isEnrolled = coSigning?.data?.org.multisig_status === MULTISIG_ORG_STATUS.ENROLLED;
  // The invited Owner is not a signer yet, so the roster cannot name them
  // here; the banner copes without a name.
  // Reconcile only once every status query has settled. On mount React Query
  // serves cached data while it refetches, so a fresh "no session" can land
  // beside a stale "not enrolled" organization and misread a setup that just
  // succeeded as one that didn't finish.
  const isStatusReady = !!coSigning?.data && !coSigning.isFetching && role === "owner";
  useEffect(() => {
    if (!isStatusReady) return;
    trackEnrollmentSession({ session, isEnrolled });
  }, [isStatusReady, session, isEnrolled]);

  if (role !== "owner") return null;

  const showOutcome = !!outcome && !outcome.dismissed && !isPaused;
  const showEnrollmentOutcome = !!enrollmentOutcome && !enrollmentOutcome.dismissed && !isEnrolled;

  const initiator = pause
    ? {
        first_name: pause.view.initiator?.first_name ?? pause.initiatorName,
        last_name: pause.view.initiator?.last_name ?? "",
      }
    : null;

  return (
    <>
      {showEnrollmentOutcome ? (
        <EnrollmentOutcomeBanner
          outcome={enrollmentOutcome}
          onDismiss={dismissEnrollmentOutcome}
          onSetUpAgain={() => {
            dismissEnrollmentOutcome();
            navigate(Routes.MULTISIG_SETUP);
          }}
        />
      ) : null}
      {showOutcome ? (
        <UnenrollmentOutcomeBanner outcome={outcome} onDismiss={dismissUnenrollmentOutcome} />
      ) : null}
      {isPaused && pause && initiator && !pause.isInitiator ? (
        <UnenrollmentPendingBanner
          initiator={initiator}
          deadline={pause.deadlineShort}
          onReview={() => navigate(Routes.MULTISIG_MANAGE)}
        />
      ) : null}
    </>
  );
};
